import Seo from "@/components/Seo";
import { Reveal } from "@/components/Motion";
import { Chapter, CtaBanner, PageHero } from "@/components/Sections";
import { COMPANY } from "@/lib/content";

const About = () => (
  <>
    <Seo
      title="Om os — Dansk B2B malerfirma i Rødovre | Al Maler Mester ApS"
      description="Al Maler Mester ApS er et dansk malerfirma registreret i Rødovre, specialiseret i professionelt malerarbejde for erhvervskunder og byggeprojekter i hele Danmark."
    />
    <PageHero
      overline="Om os"
      title={
        <>
          Et dansk malerfirma med fokus på{" "}
          <span className="font-editorial italic font-medium text-signal">erhverv</span>
        </>
      }
      lead="Al Maler Mester ApS er et dansk malerfirma, der udelukkende arbejder med professionelle erhvervskunder. Vi er registreret i Danmark med adresse i Rødovre og arbejder for virksomheder og professionelle partnere i hele landet."
      image="/images/hero.jpg"
    />

    <section className="max-w-[1440px] mx-auto px-5 md:px-10 pb-24 md:pb-32">
      <Chapter nr="01" label="Vores tilgang" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-7">
          <h2 className="font-cabinet text-3xl sm:text-4xl font-bold tracking-tighter text-balance">
            Malerarbejde er et håndværk — samarbejde er en disciplin
          </h2>
          <p className="mt-6 text-soot leading-relaxed">
            På erhvervs- og byggeprojekter handler godt malerarbejde om mere end pensel og rulle. Det handler
            om at forstå projektets helhed: tidsplanen, de øvrige fag, kravene til finish og betydningen af at
            holde, hvad der er aftalt.
          </p>
          <p className="mt-4 text-soot leading-relaxed">
            Derfor arbejder vi med klare aftaler, direkte kommunikation og en bemanding, der kan skaleres efter
            opgaven. Vi påtager os både mindre enkeltopgaver og større malerentrepriser – altid med samme
            professionelle tilgang.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="bg-ink text-bone p-8 md:p-10 grain" data-testid="about-facts">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bone/40 mb-6">Fakta</p>
            <dl className="space-y-5 text-sm">
              {[
                ["Selskab", COMPANY.name],
                ["CVR", COMPANY.cvr],
                ["Adresse", `${COMPANY.street}, ${COMPANY.city}`],
                ["Telefon", COMPANY.phone],
                ["Segment", "Udelukkende erhverv (B2B)"],
                ["Område", "Hele Danmark"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-6 border-b border-bone/10 pb-4">
                  <dt className="text-bone/50">{k}</dt>
                  <dd className="font-medium text-right">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="bg-white grain py-24 md:py-32">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <Chapter nr="02" label="Det står vi på" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
          {[
            {
              nr: "01",
              title: "Ærlig rådgivning",
              desc: "Vi siger, hvad en opgave kræver – hverken mere eller mindre. Det giver realistiske tilbud og få overraskelser.",
            },
            {
              nr: "02",
              title: "Respekt for tidsplanen",
              desc: "På bygge- og erhvervsprojekter er tid en del af kvaliteten. Vi planlægger efter jeres deadlines – ikke omvendt.",
            },
            {
              nr: "03",
              title: "Et resultat, man kan aflevere",
              desc: "Vi går ikke fra en opgave, før finish og detaljer er på plads. Det færdige arbejde skal kunne tåle synet fra både kunde og byggestyring.",
            },
          ].map((v, i) => (
            <Reveal key={v.nr} delay={i * 0.1}>
              <div data-testid={`value-card-${v.nr}`} className="bg-white p-8 md:p-10 h-full">
                <span className="font-editorial italic text-2xl text-signal">{v.nr}</span>
                <h3 className="mt-6 font-cabinet text-xl font-bold tracking-tight">{v.title}</h3>
                <p className="mt-3 text-sm text-soot leading-relaxed">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CtaBanner />
  </>
);

export default About;
